import { useQueries } from "@tanstack/react-query";

import { productsApi } from "../../api/products";
import type { ProductListItem, ProductListPage } from "../../api/types";
import { categoriesForFamily, PRODUCT_FAMILIES } from "../../catalog/taxonomy";
import type { ProductFamily } from "../../catalog/taxonomy";

/**
 * One family's products, and every family's count, from the committed admin
 * listing endpoint.
 *
 * WHY THIS EXISTS AT ALL
 * Product management is organised by family, and the endpoint is not. It
 * filters on the stored `category` with an equality test
 * (`Product.category == category`, product_service.py), so Contact Lenses and
 * Accessories are one request each and Glasses — which is `sunglasses` AND
 * `eyeglasses` — is not expressible as one. There is no stored value meaning
 * "either", and inventing one would mean a second taxonomy that could drift
 * from the one Flutter's type tabs actually read.
 *
 * The alternative was to fetch the catalogue unfiltered and split it in the
 * browser. That is the one thing this screen may not do: the list is paginated
 * server-side, so a local filter searches only the page in front of the
 * administrator and reports that a product on page two does not exist.
 *
 * So the family asks for each of its categories, and the pages are combined
 * here — server-side filtering kept, pagination kept honest.
 */

/** How many products one page of the family table shows. */
export const FAMILY_PAGE_SIZE = 25;

/** The filters a family view is narrowed by. Both are scoped to the family:
 *  they are sent with every category request the family makes, so a search
 *  never reaches out of the section the administrator is standing in. */
export interface FamilyFilters {
  search?: string;
  status?: string;
}

function listArgs(category: string, page: number, filters: FamilyFilters) {
  return {
    search: filters.search || undefined,
    status: filters.status || undefined,
    category,
    page,
    page_size: FAMILY_PAGE_SIZE,
  };
}

/**
 * The server's own ordering, restated for the one case that needs it.
 *
 * Two already-sorted pages do not concatenate into a sorted page, so a family
 * spanning two categories has to be re-ordered after the merge. This mirrors
 * `_SORTS["updated_at"]` with the `Product.id.asc()` tiebreak that follows it
 * in product_service.py — the listing offers no sort control, so that default
 * is the whole of what has to be matched. It is deliberately the ONLY ordering
 * knowledge on this side; if the screen ever gains a sort control, this is the
 * one place that has to learn about it.
 */
function byUpdatedAtDesc(a: ProductListItem, b: ProductListItem): number {
  if (a.updated_at !== b.updated_at) {
    return a.updated_at < b.updated_at ? 1 : -1;
  }
  return a.product_id < b.product_id ? -1 : a.product_id > b.product_id ? 1 : 0;
}

/** What the table needs, in the shape the single-category case already had. */
export interface FamilyProductsResult {
  page: ProductListPage | null;
  isPending: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * One page of a family's products.
 *
 * For a single-category family the server's page IS the answer and nothing is
 * recombined. For Glasses, rendering merged page N needs pages 1..N of BOTH
 * categories: the merged top N*pageSize rows can only come from the top
 * N*pageSize of each, so that is exactly enough and never more. Every one of
 * those requests is an ordinary cached query, so paging forward re-uses what
 * paging there already fetched.
 */
export function useFamilyProducts(
  family: ProductFamily,
  page: number,
  filters: FamilyFilters,
): FamilyProductsResult {
  const categories = categoriesForFamily(family);
  const pages = Array.from({ length: Math.max(1, page) }, (_, i) => i + 1);

  // One query per (category, page) it takes to answer. A single-category
  // family asks for its one page and nothing more.
  const specs =
    categories.length === 1
      ? categories.map((category) => ({ category, page }))
      : categories.flatMap((category) =>
          pages.map((p) => ({ category, page: p })),
        );

  const results = useQueries({
    queries: specs.map(({ category, page: p }) => ({
      queryKey: ["products", { ...listArgs(category, p, filters) }],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        productsApi.list(listArgs(category, p, filters), signal),
    })),
  });

  const isPending = results.some((r) => r.isPending);
  const isFetching = results.some((r) => r.isFetching);
  const error = results.find((r) => r.isError)?.error ?? null;
  const refetch = () => {
    for (const result of results) void result.refetch();
  };

  // A partial answer is not an answer: showing the categories that came back
  // while one failed would present a short list as the whole family.
  if (isPending || error) {
    return { page: null, isPending, isFetching, error, refetch };
  }

  const answers = results.map((r) => r.data).filter(Boolean) as ProductListPage[];
  if (answers.length !== specs.length) {
    return { page: null, isPending: true, isFetching, error: null, refetch };
  }

  if (categories.length === 1) {
    return { page: answers[0] ?? null, isPending, isFetching, error: null, refetch };
  }

  // The family's total is the sum of its categories' totals — each is a real
  // server count for a real filter, never a length of what happens to be
  // loaded.
  const total = categories.reduce((sum, category) => {
    const first = specs.findIndex(
      (s) => s.category === category && s.page === 1,
    );
    return sum + (first >= 0 ? (answers[first]?.total ?? 0) : 0);
  }, 0);

  const seen = new Set<string>();
  const merged: ProductListItem[] = [];
  for (const answer of answers) {
    for (const item of answer.items) {
      if (seen.has(item.product_id)) continue;
      seen.add(item.product_id);
      merged.push(item);
    }
  }
  merged.sort(byUpdatedAtDesc);

  const start = (Math.max(1, page) - 1) * FAMILY_PAGE_SIZE;
  return {
    page: {
      items: merged.slice(start, start + FAMILY_PAGE_SIZE),
      total,
      page: Math.max(1, page),
      page_size: FAMILY_PAGE_SIZE,
      total_pages: Math.max(1, Math.ceil(total / FAMILY_PAGE_SIZE)),
    },
    isPending,
    isFetching,
    error: null,
    refetch,
  };
}

/**
 * How many products each family holds under the current filters.
 *
 * Honest counts, from the listing endpoint itself: one request per stored
 * category asking for a single row, read for its `total`. Nothing is derived
 * from what the table happens to have loaded, and no aggregate endpoint is
 * invented for it.
 *
 * They follow the search and status filters on purpose. With a search active,
 * "Accessories (2)" says how many accessories match what was typed — which is
 * the number that helps an administrator decide whether to look there. A count
 * of the whole family beside a filtered table would contradict the table.
 *
 * A count is null until its request answers, so a tab shows no number rather
 * than a wrong one — and never a zero it has not earned.
 */
export function useFamilyCounts(
  filters: FamilyFilters,
): Record<ProductFamily, number | null> {
  const specs = PRODUCT_FAMILIES.flatMap((family) =>
    categoriesForFamily(family).map((category) => ({ family, category })),
  );

  const results = useQueries({
    queries: specs.map(({ category }) => ({
      queryKey: [
        "products",
        "count",
        { search: filters.search || "", status: filters.status || "", category },
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        productsApi.list(
          {
            search: filters.search || undefined,
            status: filters.status || undefined,
            category,
            page: 1,
            page_size: 1,
          },
          signal,
        ),
    })),
  });

  const counts = {} as Record<ProductFamily, number | null>;
  for (const family of PRODUCT_FAMILIES) counts[family] = 0;

  specs.forEach(({ family }, index) => {
    const total = results[index]?.data?.total;
    if (total === undefined) {
      counts[family] = null;
      return;
    }
    // A family whose other category has not answered stays unknown.
    if (counts[family] !== null) counts[family] = (counts[family] ?? 0) + total;
  });

  return counts;
}
